<script setup lang="ts">
    import { computed, ref } from 'vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import type { AdditionalField } from '../ListsTypes.d';

    const props = defineProps<{
        availableFields: AdditionalField[];
        selectedFieldNames: Set<string>;
    }>();

    const emit = defineEmits<{
        toggleField: [field: AdditionalField];
    }>();

    type Category = 'TextFields' | 'LongCustom' | 'Datefields' | 'Bools' | 'Numberfields' | 'Other';

    const CATEGORY_ORDER: Category[] = ['TextFields', 'LongCustom', 'Datefields', 'Bools', 'Numberfields', 'Other'];

    function getCategory(field: AdditionalField): Category {
        if (/^CustomLong/.test(field.name)) return 'LongCustom';
        if (field.type === 'datetime') return 'Datefields';
        if (field.type === 'boolean') return 'Bools';
        if (field.type === 'number') return 'Numberfields';
        if (field.type === 'string') return 'TextFields';
        return 'Other';
    }

    function getCategoryName(cat: Category): string {
        if (cat === 'LongCustom') return $Resources.CustomFieldsLongTextFieldsHeading;
        if (cat === 'Datefields') return $Resources.CustomFieldsDateFieldsHeading;
        if (cat === 'Bools') return $Resources.CustomFieldsBooleanFieldsHeading;
        if (cat === 'Numberfields') return $Resources.CustomFieldsNumericFieldsHeading;
        if (cat === 'TextFields') return $Resources.CustomFieldsTextFieldsHeading;
        return $Resources.OtherLabel;
    }

    const search = ref('');

    const groupedFields = computed(() => {
        const query = search.value.trim().toLowerCase();
        const fields = props.availableFields.filter((f) => {
            if (f.alwaysVisible) return false;
            if (!query) return true;
            return f.displayName.toLowerCase().includes(query) || f.name.toLowerCase().includes(query);
        });
        const groups = new Map<Category, AdditionalField[]>();
        for (const field of fields) {
            const cat = getCategory(field);
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat)!.push(field);
        }
        return CATEGORY_ORDER.filter((cat) => groups.has(cat)).map((cat) => ({ category: cat, fields: groups.get(cat)! }));
    });

    const hasSelection = computed(() => props.selectedFieldNames.size > 0);
</script>

<template>
    <BSDropdownButton menuClass="dropdown-menu-right" :closeOnClickInside="false" maxHeight="400px">
        <template #toggle>
            {{ $Resources.AdditionalFieldsMenuItemLabel }}
        </template>
        <template #content>
            <li class="dropdown-menu-content" :class="{ 'has-selected': hasSelection }">
                <h4 class="m-0-t">{{ $Resources.AdditionalFieldsDropdownHeading }}</h4>
                <BSFormSearch v-model="search" :disableDebounce="true" class="m-sm-t" @reset="search = ''" />
                <template v-for="(group, index) in groupedFields" :key="group.category">
                    <h5 :class="{ 'm-lg-t': index }">{{ getCategoryName(group.category) }}</h5>
                    <FormCheckable
                        class="m-sm-t"
                        v-for="field in group.fields"
                        :key="field.name"
                        :label="field.displayName || `[${field.name}]`"
                        :checked="selectedFieldNames.has(field.name)"
                        @update:modelValue="emit('toggleField', field)" />
                </template>
            </li>
        </template>
    </BSDropdownButton>
</template>
